import type {
  IServerSideDatasource,
  IServerSideGetRowsParams,
} from '@ag-grid-community/core';
import type { SqlResponse } from '@cdw-ae/common-data';
import type { SelectedFilterField } from '@cdw-ae/filter-bar-interfaces';
import type { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { ICdwSearchGridDataService } from '../interfaces/data-source.interface';

/**
 * Callback bag the factory needs to build an SSRM datasource without binding
 * to the component class. Each accessor is read at the moment AG-Grid calls
 * `getRows`, so live values flow through (the consumer can mutate filters or
 * swap the data service after construction and the next block fetch picks it
 * up).
 */
export interface SqlSsrmDatasourceCallbacks<
  T extends { id: string | number },
> {
  /** Snapshot of current filter selections at the moment AG-Grid asks for a block. */
  getCurrentFilters: () => SelectedFilterField[];
  /**
   * Snapshot of the data service. May return `undefined` if the consumer
   * hasn't wired one yet — in that case the datasource succeeds with empty
   * rows so AG-Grid doesn't deadlock during the early lifecycle window
   * between component construction and `@Input` binding.
   */
  getDataService: () => ICdwSearchGridDataService<T> | undefined;
  /**
   * Snapshot of the IDs that are currently in the "updating" state (e.g.
   * a job is mutating them in the background). Used to augment fetched rows
   * with `isUpdating: true` before they reach AG-Grid, so the existing
   * `PreviewIconComponent` cell renderer and `getRowClass: 'stale-row'`
   * styling pick up the state change without any cell-renderer changes.
   *
   * Optional — when omitted (or returns undefined), the datasource performs
   * no augmentation and rows pass through unchanged. This keeps the factory
   * behaviorally backwards-compatible with consumers that don't need the
   * augmentation layer (e.g. the Storybook stories, future read-only
   * consumers).
   */
  getUpdatingIds?: () => Set<string> | undefined;
  /**
   * Predicate evaluated per fetched row. When it returns true, the row is
   * augmented with `isUpdating: true` (same effect as appearing in
   * `updatingIds`). Used by the SSRM selection-lock failsafe to drive the
   * spinner from a derived condition (e.g. `row.isSelected && lockActive`)
   * without materializing the full selected-id Set on the client — which
   * could be 500k entries for large server-side selections.
   *
   * Evaluated AFTER the updatingIds check; either source can mark a row as
   * updating. Optional — omit for non-lock consumers.
   */
  getSelectedRowSpinnerPredicate?: () => ((row: unknown) => boolean) | undefined;
  /**
   * Snapshot of the field paths the consumer wants the backend to project
   * to. Forwarded into `fetchSqlData(...)` params on every block fetch, so
   * a consumer that derives this from the currently-visible column set
   * (the SSRM grid's `computeSelectFields()` does exactly that) can
   * shrink wire payload by 50–90% per row vs. always shipping full
   * entities.
   *
   * Optional — when omitted (or returns undefined / empty array), the
   * factory passes nothing for `selectFields` and the backend returns
   * full entities (current/legacy behavior, backwards compatible).
   *
   * Per the backend contract from Phases 2/3, the backend also unions
   * its own ALWAYS_INCLUDED set (e.g. `id`, `isStale`) into the
   * projection — so omitting those from the consumer-side list is safe;
   * they'll come through anyway.
   */
  getSelectFields?: () => string[] | undefined;
  /**
   * Snapshot of the consumer-owned pending in-cell edits, keyed by row id →
   * field path → new value. Read on every block fetch. The factory overlays
   * each pending value onto the matching fetched row's `node.data` BEFORE
   * AG-Grid sees it, so a user's unsaved edit value is preserved across
   * SSRM block eviction + refetch (the cellValueChanged handler in the
   * library tracks WHICH cells were modified, but the actual value lives
   * here so it can be re-applied on every refetch).
   *
   * Field paths use dotted notation (e.g. `vendorProperties.google.notes`)
   * so the overlay must walk into nested objects and only mutate the leaf.
   *
   * Optional — when omitted (or returns undefined / empty Map), no overlay
   * happens and rows pass through unchanged.
   */
  getPendingEdits?: () => Map<string, Map<string, unknown>> | undefined;
  /**
   * Snapshot of the "show selected only" toggle. When true, the factory
   * forwards `useSelectionAsFilter: true` to `fetchSqlData` and the data
   * service translates that to a query string param. The backend then
   * short-circuits to `WHERE id = ANY($selected) ORDER BY ... LIMIT/OFFSET`
   * using ids read from the user's server-side selection set in Redis —
   * no id array crosses the wire (the L1 latent bug from PR #885 dies
   * by construction with this flag).
   *
   * Optional — when omitted (or returns false), the normal filter chain
   * applies and the grid behaves as a standard SSRM datasource.
   */
  getUseSelectionAsFilter?: () => boolean;
  /**
   * EDUS-2509: snapshot of the consumer's `idAllowList$` emission. When
   * the returned array is non-empty, the factory forwards it to
   * `fetchSqlData` as `idAllowList`; the data service translates the
   * array into the backend's existing `activeFilters: JSON.stringify(ids)`
   * wire format (handled by `normalizeInputToQueryDto` → `mapAssetIdsToQueryDto`).
   * Used by the "Show changes only" UX in device-search.
   *
   * Optional — when omitted (or returns undefined / empty), the normal
   * filter chain applies.
   */
  getIdAllowList?: () => string[] | undefined;
  /**
   * Snapshot of the grid's per-instance session identifier. Sourced from
   * `sessionStorage[grid-session:${viewKey}]` and minted on first mount per
   * (viewKey) by the grid component. Forwarded into `IGetSqlDataParams` as
   * `gridSessionId`; the data service is responsible for converting it to
   * the `x-cdw-grid-session` HTTP header on its outgoing call.
   *
   * Optional — when omitted or returns undefined, the data service receives
   * no `gridSessionId` and the backend falls back to its non-selection-aware
   * code path (full backwards compat for non-grid callers and Storybook
   * stories).
   */
  getGridSessionId?: () => string | undefined;
  /**
   * Lifecycle teardown signal. Each fetch subscription pipes through
   * `takeUntil(destroy$)` so destroyed components don't race to call AG-Grid
   * back with stale responses.
   */
  destroy$: Observable<void>;
  /**
   * Used to build the synthesized fallback Error message
   * (`Failed to retrieve {label}s`) when `fetchSqlData` rejects with a
   * non-Error value. Read live so consumers can rebrand at runtime.
   */
  getItemLabel: () => string;
  /** Invoked on every successful response, after `params.success(...)`. */
  onDataLoaded: (info: { itemTotal: number; hasStaleData: boolean }) => void;
  /**
   * Invoked on every successful response that includes `selectionCount`
   * (server-side selection store, EDUS-2484). Drives the grid's
   * `(selectionCount)` `@Output` so consumers can mirror the count into
   * their state service / footer without a separate GET round-trip.
   *
   * Optional — when omitted, the count is silently dropped. Non-emit when
   * the response lacks `selectionCount` (legacy non-selection-aware backend
   * or no `gridSessionId` was supplied).
   */
  onSelectionCount?: (count: number) => void;
  /** Invoked on error, after `params.fail()`. */
  onError: (err: Error) => void;
}

/**
 * Translate AG-Grid's native sort model into the consumer-facing sortOrder
 * shape (`{name, direction}` with uppercased direction). Pure data
 * transformation, no AG-Grid runtime dependency, isolated for testability.
 */
export const translateSortModel = (
  sortModel: { colId: string; sort?: string | null }[] | undefined
): { name: string; direction: 'ASC' | 'DESC' }[] | undefined =>
  sortModel?.map((s) => ({
    name: s.colId,
    direction: s.sort?.toUpperCase() as 'ASC' | 'DESC',
  }));

/**
 * Augment a row with `isUpdating: true` when its id appears in the external
 * updating-IDs set. Additive only — when the row is NOT in the set, the row
 * is returned untouched (the server-provided `isStale` flag and any
 * pre-existing `isUpdating` flag are preserved). The augmentation never
 * resets `isUpdating` to `false`, so a row that was already in the
 * "updating" state by server declaration stays that way.
 *
 * Returns the same reference when no mutation is needed — saves AG-Grid's
 * change detection from churning on rows that didn't actually change.
 *
 * Exported so the factory spec can exercise it directly.
 */
export const augmentRowWithUpdatingFlag = <
  T extends { id: string | number; isUpdating?: boolean },
>(
  row: T,
  updatingIds: Set<string> | undefined
): T => {
  if (!updatingIds || updatingIds.size === 0) return row;
  if (!updatingIds.has(String(row.id))) return row;
  if (row.isUpdating) return row;
  return { ...row, isUpdating: true };
};

/**
 * Predicate-based variant of `augmentRowWithUpdatingFlag`. Used by the SSRM
 * selection-lock failsafe to mark currently-selected rows as updating without
 * materializing the full selected-id Set client-side. Same return-same-
 * reference contract: returns `row` unchanged when the predicate is absent,
 * returns false, or the row is already flagged.
 *
 * Exported so the factory spec can exercise it directly.
 */
export const augmentRowWithLockedSpinner = <
  T extends { id: string | number; isUpdating?: boolean },
>(
  row: T,
  predicate: ((row: unknown) => boolean) | undefined
): T => {
  if (!predicate) return row;
  if (row.isUpdating) return row;
  if (!predicate(row)) return row;
  return { ...row, isUpdating: true };
};

/**
 * Mutate `obj` in place so the leaf at the dotted `path` is set to `value`.
 * Walks/creates intermediate objects as needed: `setByDottedPath(o,
 * 'a.b.c', 1)` produces `{a: {b: {c: 1}}}` regardless of whether `o.a`
 * or `o.a.b` existed before. Non-object intermediates (e.g. a primitive
 * sitting where we expected an object) are replaced with empty objects —
 * this is a write-only API and shouldn't try to be clever about
 * conflict resolution.
 *
 * Exported so the factory spec can exercise it directly.
 */
export const setByDottedPath = (
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): void => {
  const segments = path.split('.');
  let cursor = obj;
  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    const next = cursor[segment];
    if (next == null || typeof next !== 'object') {
      cursor[segment] = {};
    }
    cursor = cursor[segment] as Record<string, unknown>;
  }
  cursor[segments[segments.length - 1]] = value;
};

/**
 * Overlay the consumer's pending in-cell edits onto a freshly-fetched row.
 * For each `[fieldPath, newValue]` in `pendingEdits.get(String(row.id))`,
 * mutates the leaf at `fieldPath` to `newValue`. Field paths support
 * dotted notation (`vendorProperties.google.notes`) for nested entity
 * structures.
 *
 * Returns the same reference when no edits exist for this row — saves
 * AG-Grid's change detection from churning. When edits exist, returns a
 * SHALLOW COPY (so we don't mutate the server's response object) and
 * applies the edits to the copy. Nested objects ARE mutated in the copy
 * via `setByDottedPath` since they share references with the server
 * response — but this is safe because the server response is consumed
 * once per fetch and immediately discarded.
 *
 * Exported so the factory spec can exercise it directly.
 */
export const applyPendingEditsToRow = <T extends { id: string | number }>(
  row: T,
  pendingEdits: Map<string, Map<string, unknown>> | undefined
): T => {
  if (!pendingEdits || pendingEdits.size === 0) return row;
  const rowEdits = pendingEdits.get(String(row.id));
  if (!rowEdits || rowEdits.size === 0) return row;
  const next = { ...row } as Record<string, unknown>;
  for (const [field, value] of rowEdits) {
    setByDottedPath(next, field, value);
  }
  return next as T;
};

/**
 * Build a pure SSRM datasource from a callback bag. The returned object is
 * what AG-Grid expects from `gridApi.setServerSideDatasource(...)`.
 *
 * Behavioral guarantees:
 *   - `getRows` translates `request.{startRow, endRow, sortModel}` into the
 *     consumer's `IGetSqlDataParams` shape.
 *   - On success: augments rows with `isUpdating` from the external set
 *    , then calls `params.success({rowData, rowCount})` AND emits
 *     `onDataLoaded({itemTotal, hasStaleData})`.
 *   - On error: calls `params.fail()` AND emits `onError(err)` with the
 *     original Error (or a synthesized one for non-Error throws).
 *   - With no data service wired: succeeds gracefully with empty rows
 *     (no `onDataLoaded` emit; no `onError`).
 *   - All subscriptions pipe through `takeUntil(destroy$)`.
 */
export const buildSqlSsrmDatasource = <
  T extends { id: string | number; isUpdating?: boolean },
>(
  callbacks: SqlSsrmDatasourceCallbacks<T>
): IServerSideDatasource => ({
  getRows: (params: IServerSideGetRowsParams<T>): void => {
    const { startRow, endRow, sortModel } = params.request;
    const sortOrder = translateSortModel(sortModel);

    const dataService = callbacks.getDataService();
    if (!dataService?.fetchSqlData) {
      params.success({ rowData: [], rowCount: 0 });
      return;
    }

    const selectFields = callbacks.getSelectFields?.();
    const useSelectionAsFilter = callbacks.getUseSelectionAsFilter?.();
    const idAllowList = callbacks.getIdAllowList?.();
    const gridSessionId = callbacks.getGridSessionId?.();
    dataService
      .fetchSqlData({
        activeFilters: callbacks.getCurrentFilters(),
        supplementalFilters: [],
        startRow,
        endRow,
        sortOrder,
        // Only include selectFields when the consumer actually provided a
        // non-empty list. Sending `undefined` (or omitting the key) lets
        // the backend default to full-entity projection — its existing
        // backwards-compat behavior since the original migration.
        ...(selectFields && selectFields.length > 0 ? { selectFields } : {}),
        // Forward the "show selected only" flag when true so the data
        // service includes `useSelectionAsFilter=true` in its query string.
        // The backend (Phase 3) then filters results to only the rows
        // present in the user's server-side Redis selection set. Omitted
        // when false/undefined so the normal filter chain applies and
        // legacy consumers (Storybook, future read-only views) see no
        // change in behavior.
        ...(useSelectionAsFilter ? { useSelectionAsFilter: true } : {}),
        // EDUS-2509: forward the consumer-supplied id allow-list. The data
        // service is responsible for translating it into the backend's
        // expected wire format (currently `activeFilters: JSON.stringify(ids)`
        // routed through `mapAssetIdsToQueryDto`). Omitted when empty so
        // the normal filter chain applies.
        ...(idAllowList && idAllowList.length > 0 ? { idAllowList } : {}),
        // Forward the grid session ID when present so the consumer's data
        // service can attach the `x-cdw-grid-session` HTTP header. Omitted
        // when no session has been minted (Storybook stories, server-side
        // pre-render, etc.) so the backend stays backwards-compatible.
        ...(gridSessionId ? { gridSessionId } : {}),
      })
      .pipe(takeUntil(callbacks.destroy$))
      .subscribe({
        next: (response: SqlResponse<T>): void => {
          const updatingIds = callbacks.getUpdatingIds?.();
          const lockedSpinnerPredicate =
            callbacks.getSelectedRowSpinnerPredicate?.();
          const pendingEdits = callbacks.getPendingEdits?.();
          const rawRows = response.data ?? [];
          // Three-pass augmentation:
          //   1. isUpdating flag from the explicit Set (force-refresh path)
          //   2. isUpdating flag from the lock predicate (selection-lock path)
          //   3. pending in-cell edit values — preserves user's unsaved values
          //      across block evict + refetch.
          // Each helper returns the SAME reference when no work is needed,
          // so unaffected rows skip AG-Grid change detection.
          const rowData = rawRows
            .map((row) => augmentRowWithUpdatingFlag(row, updatingIds))
            .map((row) =>
              augmentRowWithLockedSpinner(row, lockedSpinnerPredicate)
            )
            .map((row) => applyPendingEditsToRow(row, pendingEdits));
          params.success({
            rowData,
            rowCount: response.itemTotal,
          });
          callbacks.onDataLoaded({
            itemTotal: response.itemTotal,
            hasStaleData: !!response.meta?.hasStaleData,
          });
          // Surface server-side selection count when present. Skip the emit
          // entirely (rather than emit 0) when the field is absent so the
          // consumer can distinguish "no selection store wired" from "store
          // wired but empty selection".
          if (
            response.selectionCount !== undefined &&
            callbacks.onSelectionCount
          ) {
            callbacks.onSelectionCount(response.selectionCount);
          }
        },
        error: (err: unknown): void => {
          params.fail();
          callbacks.onError(
            err instanceof Error
              ? err
              : new Error(`Failed to retrieve ${callbacks.getItemLabel()}s`)
          );
        },
      });
  },
});
